import { dialogClass, type DialogSize } from "@/components/layout-2/dialog-size";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { cn } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { feedingClient } from "./api";
import type { DraftChange } from "./draft";
import { ImpactReview } from "./impact-review";
import type { FeedingPage, FeedingPageProps } from "./types";

/** The steps of the configuration, in the order of the first setup. */
export const STEPS = ["records", "pace", "rules", "exclusions"] as const;
export type StepCode = (typeof STEPS)[number];

/** What a step would save: the body of the Save (only what it changes) and its changes, as the impact lists them. */
export type StepDraft = { body: Record<string, unknown>; changes: DraftChange[] };

/** Where the dialog sits: an edit from the overview, or a step of the first setup (`index` from 1). */
export type StepContext = { setup: { index: number; total: number } | null };

/**
 * One step of the Feeding configuration in its own dialog, saved on its own. The content edits a local
 * draft; the footer saves it. A campaign in service goes through the impact of the step first, inside
 * the same dialog (no dialog on top of a dialog), and its Save waits for the box to be ticked.
 * During the first setup, the step saves and hands over to the next one (`onDone`); without a change
 * it just moves on.
 */
export function StepDialog({
    title,
    description,
    size = "md",
    context,
    page,
    send,
    onClose,
    onDone,
    prepare,
    dirty,
    canSkip = true,
    footerStart,
    footer,
    testId,
    children
}: {
    title: React.ReactNode;
    description?: React.ReactNode;
    size?: DialogSize;
    context: StepContext;
    page: FeedingPage;
    send: FeedingPageProps["send"];
    onClose: () => void;
    /** After a Save, or a step left as it is during the first setup. */
    onDone: () => void;
    /** The draft of the step when the user saves, or null when it is not valid yet (the content shows why). */
    prepare: () => StepDraft | null;
    /** Something to save: without it the Save is disabled (the first setup moves on instead). */
    dirty: boolean;
    /** During the first setup, whether the step can be left as it is (a source must be chosen). */
    canSkip?: boolean;
    /** Left of the footer: a count, a hint. */
    footerStart?: React.ReactNode;
    /** Replaces the whole footer while the content is in a sub-step of its own (picking a ListView to add). */
    footer?: React.ReactNode;
    testId?: string;
    children: React.ReactNode;
}) {
    const [review, setReview] = useState<StepDraft | null>(null);
    const [impactReady, setImpactReady] = useState<{ risky: boolean } | null>(null);
    const [acknowledged, setAcknowledged] = useState(false);
    const [pause, setPause] = useState(false);
    const [saving, setSaving] = useState(false);
    const setup = context.setup;

    const save = async (draft: StepDraft, impactAcknowledged: boolean) => {
        setSaving(true);
        const paused = impactAcknowledged && pause;
        const body = { ...draft.body, ...(impactAcknowledged ? { impact_acknowledged: true } : {}), ...(paused ? { pause_campaign: true } : {}) };
        const message = paused ? "Saved — the campaign is paused until you resume it" : page.state === "active" ? "Saved — applies from the next pass" : "Saved";
        const saved = await send(feedingClient(page.campaign.code).put(body).json<FeedingPage>(), message);
        setSaving(false);
        if (saved) onDone();
    };
    const submit = () => {
        if (!dirty) {
            if (setup && canSkip) onDone();
            return;
        }
        const draft = prepare();
        if (!draft) return;
        if (page.needs_impact) {
            setImpactReady(null);
            setReview(draft);
        } else void save(draft, false);
    };

    const primaryLabel = page.needs_impact && dirty ? "Review impact" : setup ? (dirty ? "Save and continue" : "Continue") : "Save";

    return (
        <Dialog open onOpenChange={open => !open && !saving && onClose()}>
            <DialogContent className={dialogClass(size)} data-testid={testId}>
                <DialogHeader className="flex-shrink-0">
                    {(setup || page.needs_impact) && (
                        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600" data-testid="step-progress">
                            {setup && <span>First setup · step {setup.index} of {setup.total}</span>}
                            {setup && page.needs_impact && <span aria-hidden>·</span>}
                            {page.needs_impact && (
                                <>
                                    <span className={cn(!review && "text-blue-800")}>1. Edit</span>
                                    <span aria-hidden className="text-gray-400">
                                        ›
                                    </span>
                                    <span className={cn(review && "text-blue-800")}>2. Review impact</span>
                                </>
                            )}
                        </div>
                    )}
                    <DialogTitle>{title}</DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>

                {/* The edit stays mounted under the review: Back finds it as it was. */}
                <div className={cn("flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto", review && "hidden")} data-testid="step-body">
                    {children}
                </div>
                {review && (
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                        <ImpactReview
                            campaignCode={page.campaign.code}
                            body={review.body}
                            changes={review.changes}
                            acknowledged={acknowledged}
                            onAcknowledgedChange={setAcknowledged}
                            pause={pause}
                            onPauseChange={setPause}
                            onImpact={impact => setImpactReady(impact ? { risky: impact.risky } : null)}
                        />
                    </div>
                )}

                {footer && !review ? (
                    <DialogFooter className="flex-shrink-0 items-center gap-2 sm:justify-between sm:gap-0">{footer}</DialogFooter>
                ) : (
                    <DialogFooter className="flex-shrink-0 items-center gap-2 sm:justify-between sm:gap-0">
                        <div className="text-sm text-gray-600">
                            {review ? (
                                <Button type="button" variant="ghost" onClick={() => setReview(null)} disabled={saving} data-testid="step-back">
                                    <ChevronLeft className="mr-1 h-4 w-4" /> Back to edit
                                </Button>
                            ) : (
                                footerStart
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                                {setup ? "Finish later" : "Cancel"}
                            </Button>
                            {review ? (
                                <SimpleTooltip asChild message={impactReady ? (acknowledged ? "Save the change" : "Tick the box to confirm") : "Waiting for the impact"}>
                                    <span>
                                        <Button
                                            type="button"
                                            variant={impactReady?.risky ? "destructive" : "default"}
                                            disabled={!impactReady || !acknowledged || saving}
                                            onClick={() => save(review, true)}
                                            data-testid="impact-confirm"
                                        >
                                            {pause ? "Save and pause" : setup ? "Save and continue" : "Save"}
                                        </Button>
                                    </span>
                                </SimpleTooltip>
                            ) : (
                                <Button type="button" onClick={submit} disabled={saving || (!dirty && !(setup && canSkip))} data-testid="step-save">
                                    {primaryLabel}
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
